import { API_LANGUAGE } from '../../api/client';
import { timeAgo } from '../../utils/format';
import { formatAmsterdam } from './amsterdamTime';

/** Pure helpers turning scan API data into what the Scans screens render. */

const LOCALE = API_LANGUAGE === 'du' ? 'nl-NL' : 'en-GB';
const euro = (digits) => new Intl.NumberFormat(LOCALE, { style: 'currency', currency: 'EUR', minimumFractionDigits: digits, maximumFractionDigits: digits });
const priceFormat = euro(3);
const amountFormat = euro(2);
const volumeFormat = new Intl.NumberFormat(LOCALE, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const isNum = (v) => typeof v === 'number' && Number.isFinite(v);

/** 2.429 → "€ 2,429" (nl) / "€2.429" (en). */
export const formatPrice = (v) => (isNum(v) ? priceFormat.format(v) : '—');
/** 107.8 → "€ 107,80". */
export const formatAmount = (v) => (isNum(v) ? amountFormat.format(v) : '—');
/** 44.38 → "44,38 L". */
export const formatVolume = (v, unit = 'L') => (isNum(v) ? `${volumeFormat.format(v)} ${unit || 'L'}` : '—');
export const formatNumber = (v, digits) =>
  isNum(v) ? new Intl.NumberFormat(LOCALE, { minimumFractionDigits: digits, maximumFractionDigits: digits }).format(v) : '—';

export const STATUSES = [
  { key: 'pending', label: 'Pending Review', chip: 'Pending', tone: 'pending' },
  { key: 'approved', label: 'Approved', chip: 'Approved', tone: 'ok' },
  { key: 'rejected', label: 'Rejected', chip: 'Rejected', tone: 'alert' },
  { key: 'draft', label: 'Draft', chip: 'Drafts', tone: 'neutral' },
];
export const STATUS_KEYS = STATUSES.map((s) => s.key);
export const statusMeta = (key) => STATUSES.find((s) => s.key === key) ?? { key, label: key ?? '—', chip: key, tone: 'neutral' };

export const CONFIDENCE = {
  high: { label: 'High', tone: 'ok' },
  medium: { label: 'Medium', tone: 'pending' },
  low: { label: 'Low', tone: 'alert' },
};

const ENGINE_LABELS = { tesseract: 'Tesseract', textract: 'AWS Textract', none: 'None (OCR failed)' };
export const engineLabel = (engine) => ENGINE_LABELS[engine] ?? engine ?? '—';

export const scanRef = (id) => `#SCN-${id}`;

/** Only http(s) images/links are rendered. */
export function safeUrl(url) {
  if (typeof url !== 'string') return null;
  try {
    const parsed = new URL(url.trim());
    return parsed.protocol === 'https:' || parsed.protocol === 'http:' ? parsed.href : null;
  } catch {
    return null;
  }
}

/**
 * Station name for the list and header: Google's name (+ city), else the first part of what
 * OCR read on the receipt, else the placeId. `googleUnavailable` flags a non-OK Google status.
 * @param {import('./scanSchema').Scan} scan
 */
export function stationLabel(scan) {
  const google = scan.station;
  const googleUnavailable = Boolean(google?.googleStatus) && google.googleStatus.toUpperCase() !== 'OK';
  if (google?.name) return { name: google.name, city: google.city ?? null, fromReceipt: false, googleUnavailable };
  const receipt = scan.ocr.stationText?.split(/[\n,]/).map((s) => s.trim()).find(Boolean);
  if (receipt) return { name: receipt, city: google?.city ?? null, fromReceipt: true, googleUnavailable };
  return { name: scan.placeId ?? 'Unknown station', city: null, fromReceipt: false, googleUnavailable, isPlaceId: Boolean(scan.placeId) };
}

const unitShort = (unit) => (unit ? `/ ${unit}` : '/ L');

/** The date the list shows: drafts haven't been submitted yet. */
const listDate = (scan) => (scan.status === 'draft' ? scan.createdAt : scan.submittedAt ?? scan.createdAt);

/**
 * Newest first by the list's date. The API has no sort option and returns pending scans
 * oldest first, so this only orders the page it sent; undated scans go last.
 */
export function sortNewestFirst(scans) {
  const time = (scan) => Date.parse(listDate(scan)) || 0;
  return [...scans].sort((a, b) => time(b) - time(a) || b.id - a.id);
}

/** @param {import('./scanSchema').Scan} scan */
export function toRowView(scan) {
  const station = stationLabel(scan);
  const warnings = [];
  if (scan.stationMatch === false) warnings.push('Receipt postcode differs from the station');
  if (scan.ocr.confidence === 'low') warnings.push('Low OCR confidence');
  return {
    id: scan.id,
    ref: scanRef(scan.id),
    date: formatAmsterdam(listDate(scan)),
    station,
    product: scan.fuelOption?.name ?? 'Unknown fuel',
    price: isNum(scan.unitPrice) ? `${formatPrice(scan.unitPrice)} ${unitShort(scan.fuelOption?.unit)}` : '—',
    user: { name: scan.user.name?.trim() || '—', userId: scan.user.userId },
    status: statusMeta(scan.status),
    warnings,
  };
}

const yesNo = (v) => (v === true ? 'YES' : v === false ? 'NO' : '—');

/** OCR stat tiles: passes, price found, fuel matched, math check. */
export function ocrStats(ocr) {
  const c = ocr.checks ?? {};
  return [
    { key: 'passes', label: 'Passes', value: typeof c.passes === 'number' ? String(c.passes) : '—', tone: 'neutral' },
    { key: 'price', label: 'Price Found', value: yesNo(c.priceFound), tone: c.priceFound ? 'ok' : c.priceFound === false ? 'alert' : 'neutral' },
    {
      key: 'fuel',
      label: 'Fuel Matched',
      value: c.fuelAmbiguous ? 'Ambiguous' : yesNo(c.fuelMatched),
      tone: c.fuelAmbiguous ? 'pending' : c.fuelMatched ? 'ok' : c.fuelMatched === false ? 'alert' : 'neutral',
    },
    {
      key: 'math',
      label: 'Math Check',
      value: c.totalsMatch === true ? 'OK' : c.totalsMatch === false ? 'Mismatch' : '—',
      tone: c.totalsMatch ? 'ok' : c.totalsMatch === false ? 'alert' : 'neutral',
    },
  ];
}

const SOURCE_LABELS = { admin: 'admin', scan: 'scan', user: 'user', import: 'import' };

/** "Station now: € 2,399 (admin, 3 days ago)" or null when there's no stored price. */
export function describeCurrentPrice(current) {
  if (!current || !isNum(current.latestPrice)) return null;
  const parts = [SOURCE_LABELS[current.source] ?? current.source, current.updatedAt ? timeAgo(current.updatedAt) : null].filter(Boolean);
  return `${formatPrice(current.latestPrice)}${parts.length ? ` (${parts.join(', ')})` : ''}`;
}
